import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  LinearProgress,
  Chip,
  Avatar,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Article as ArticleIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Visibility as VisibilityIcon,
  Public as PublicIcon,
  Campaign as CampaignIcon,
  Payment as PaymentIcon,
  Analytics as AnalyticsIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Notifications as NotificationsIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
  Whatshot as WhatshotIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getCampaignStats } from '../../services/campaignApi';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { getPaymentHistory, getUserCreditsApi, UserCreditsResponse } from '../../services/paymentApi';

const API_BASE_URL = getApiBaseUrl();

interface DashboardStats {
  totalPRs: number;
  publishedPRs: number;
  pendingPRs: number;
  totalImpressions: number;
  totalClicks: number;
  totalShares: number;
  totalSessions: number;
  totalBacklinks: number;
  totalConversions: number;
  averageCTR: number;
  activeCampaigns: number;
  totalSpent: number;
  totalBudget: number;
  successRate: number;
  roi: number;
  publishedArticlesCount: number;
  distributedPRs: number;
}

interface RecentActivity {
  id: string;
  type: 'pr_created' | 'pr_published' | 'payment' | 'campaign';
  title: string;
  timestamp: string;
  status: string;
}

interface UpcomingTask {
  task: string;
  deadline: string;
  priority: 'high' | 'medium' | 'low';
  type?: string;
  prId?: string;
  distributionId?: string;
}

interface QuickStat {
  label: string;
  value: string | number;
  change: number;
  icon: React.ReactNode;
  color: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalPRs: 0,
    publishedPRs: 0,
    pendingPRs: 0,
    totalImpressions: 0,
    totalClicks: 0,
    totalShares: 0,
    totalSessions: 0,
    totalBacklinks: 0,
    totalConversions: 0,
    averageCTR: 0,
    activeCampaigns: 0,
    totalSpent: 0,
    totalBudget: 0,
    successRate: 0,
    roi: 0,
    publishedArticlesCount: 0,
    distributedPRs: 0,
  });

  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [upcomingTasks, setUpcomingTasks] = useState<UpcomingTask[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [creditsData, setCreditsData] = useState<UserCreditsResponse | null>(null);
  const [campaignStatsData, setCampaignStatsData] = useState<{
    totalCampaigns: number;
    activeCampaigns: number;
    totalSpent: number;
    totalBudget: number;
    totalImpressions: number;
  } | null>(null);

  // Fetch dashboard data from backend
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      // Fetch dashboard analytics, campaign stats, payment history, and credits in parallel
      const [dashboardResponse, campaignStatsResponse, paymentHistoryResponse, creditsResponse] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/pr/analytics/dashboard`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
        getCampaignStats().catch((err) => {
          console.error('Error fetching campaign stats:', err);
          return null;
        }), // Campaign stats are optional
        getPaymentHistory(1, 1000).catch(() => null), // Fetch all payments to calculate total spent
        getUserCreditsApi().catch(() => null), // Fetch credits to show credit usage
      ]);

      // Extract data from responses
      const dashboardData = dashboardResponse.status === 'fulfilled' 
        ? (dashboardResponse.value.data?.dashboard || dashboardResponse.value.data)
        : {};
      const campaignStats = campaignStatsResponse.status === 'fulfilled' 
        ? (campaignStatsResponse.value || null)
        : null;
      
      // Store campaign stats for display
      if (campaignStats) {
        setCampaignStatsData(campaignStats);
      }
      const paymentHistory = paymentHistoryResponse.status === 'fulfilled' 
        ? paymentHistoryResponse.value 
        : null;
      const creditsDataResponse = creditsResponse.status === 'fulfilled' 
        ? creditsResponse.value 
        : null;
      
      // Store credits data in state for use in render
      if (creditsDataResponse?.success) {
        setCreditsData(creditsDataResponse);
      } else {
        setCreditsData(null);
      }

      // Safely extract data with fallbacks
      const overview = dashboardData?.overview || {};
      const performance = dashboardData?.performance || {};

      // Calculate success rate (published PRs / total PRs * 100)
      const totalPRs = overview.totalPRs || 0;
      const publishedPRs = overview.publishedPRs || 0;
      const pendingPRs = overview.pendingPRs || 0;
      const distributedPRs = overview.distributedPRs || 0;
      const publishedArticlesCount = overview.publishedArticlesCount || 0;
      const successRate =
        totalPRs > 0 ? Math.round((publishedPRs / totalPRs) * 100) : 0;

      // Calculate total spent from actual payment history (completed payments only)
      let totalSpent = 0;
      if (paymentHistory?.success && paymentHistory.payments) {
        totalSpent = paymentHistory.payments
          .filter((payment: any) => payment.status === 'completed')
          .reduce((sum: number, payment: any) => {
            // Use amount from payment, or calculate from channels if amount is 0
            const amount = payment.amount || 0;
            if (amount > 0) {
              return sum + amount;
            }
            // Fallback: calculate from channels if amount is missing
            if (payment.channels && payment.channels.length > 0) {
              const channelTotal = payment.channels.reduce((channelSum: number, channel: any) => 
                channelSum + (channel.price || 0), 0
              );
              return sum + channelTotal;
            }
            return sum;
          }, 0);
      }
      
      // Fallback to campaign stats if payment history is not available
      if (totalSpent === 0 && campaignStats?.totalSpent) {
        totalSpent = campaignStats.totalSpent;
      }

      // Calculate total budget from campaign budgets (only if there are active campaigns)
      // Only use campaign budget if there are active campaigns, otherwise it's misleading
      const totalBudget = (campaignStats && campaignStats.activeCampaigns > 0 && campaignStats.totalBudget) 
        ? campaignStats.totalBudget 
        : 0;

      // Calculate ROI (improved: based on multiple metrics)
      // ROI = ((Value - Cost) / Cost) * 100
      // Value calculation includes:
      // - Impressions: $0.05 per impression (media value)
      // - Clicks: $0.50 per click (engagement value)
      // - Conversions: $10.00 per conversion (business value)
      // - Backlinks: $5.00 per backlink (SEO value)
      const totalImpressions = performance.totalImpressions || 0;
      const totalClicks = performance.totalClicks || 0;
      const totalConversions = performance.totalConversions || 0;
      const totalBacklinks = performance.totalBacklinks || 0;
      
      // Calculate estimated value from multiple sources
      const impressionValue = totalImpressions * 0.05; // $0.05 per impression
      const clickValue = totalClicks * 0.50; // $0.50 per click
      const conversionValue = totalConversions * 10.00; // $10.00 per conversion
      const backlinkValue = totalBacklinks * 5.00; // $5.00 per backlink
      const estimatedValue = impressionValue + clickValue + conversionValue + backlinkValue;
      
      // Only calculate ROI if we have meaningful engagement data
      // If no impressions/clicks/conversions, ROI calculation is not meaningful
      // Don't show negative ROI - cap at 0% minimum
      const hasEngagementData = totalImpressions > 0 || totalClicks > 0 || totalConversions > 0;
      const calculatedRoi = (totalSpent > 0 && hasEngagementData) 
        ? Math.round(((estimatedValue - totalSpent) / totalSpent) * 100) 
        : 0;
      // Cap ROI at 0% minimum - don't show negative values
      const roi = Math.max(0, calculatedRoi);

      // Update stats with all real data from backend
      setStats({
        totalPRs: totalPRs,
        publishedPRs: publishedPRs,
        pendingPRs: pendingPRs,
        distributedPRs: distributedPRs,
        totalImpressions: totalImpressions,
        totalClicks: performance.totalClicks || 0,
        totalShares: performance.totalShares || 0,
        totalSessions: performance.totalSessions || 0,
        totalBacklinks: performance.totalBacklinks || 0,
        totalConversions: performance.totalConversions || 0,
        averageCTR: performance.averageCTR || 0,
        activeCampaigns: campaignStats?.activeCampaigns || overview.activeCampaigns || 0,
        totalSpent: totalSpent,
        totalBudget: totalBudget,
        successRate: successRate,
        roi: roi,
        publishedArticlesCount: publishedArticlesCount,
      });

      // Log campaign stats for debugging
      if (campaignStats) {
        console.log('📊 Campaign Stats:', {
          totalCampaigns: campaignStats.totalCampaigns,
          activeCampaigns: campaignStats.activeCampaigns,
          totalSpent: campaignStats.totalSpent,
          totalBudget: campaignStats.totalBudget,
          totalImpressions: campaignStats.totalImpressions
        });
      }

      // Format recent activity from API response
      if (dashboardData.recentActivity && Array.isArray(dashboardData.recentActivity) && dashboardData.recentActivity.length > 0) {
        const formattedActivity: RecentActivity[] = dashboardData.recentActivity.map((activity: any) => {
          // Determine activity type based on status
          let activityType: 'pr_created' | 'pr_published' | 'payment' | 'campaign' = 'pr_created';
          if (activity.status === 'published') {
            activityType = 'pr_published';
          } else if (activity.status === 'distributed') {
            activityType = 'campaign';
          }

          // Use updatedAt if available, otherwise createdAt
          const activityDate = activity.updatedAt ? new Date(activity.updatedAt) : new Date(activity.createdAt);
          const now = new Date();
          const diffInMs = now.getTime() - activityDate.getTime();
          const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
          const diffInDays = Math.floor(diffInHours / 24);
          
          let timestamp = '';
          if (diffInMs < 60000) {
            timestamp = 'Just now';
          } else if (diffInHours < 1) {
            const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
            timestamp = `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
          } else if (diffInHours < 24) {
            timestamp = `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
          } else if (diffInDays < 7) {
            timestamp = `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
          } else {
            timestamp = activityDate.toLocaleDateString();
          }

          // Format status for display
          const statusDisplay = activity.status
            ? activity.status.charAt(0).toUpperCase() + activity.status.slice(1).replace('_', ' ')
            : 'Unknown';

          return {
            id: activity.id || activity._id || `activity-${Date.now()}`,
            type: activityType,
            title: activity.title || 'Press Release',
            timestamp: timestamp,
            status: statusDisplay,
          };
        });
        setRecentActivity(formattedActivity);
      } else {
        // If no recent activity from dashboard, try to fetch from timeline
        try {
          const timelineResponse = await axios.get(`${API_BASE_URL}/api/timeline/events`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            params: {
              page: 1,
              limit: 10
            }
          });

          if (timelineResponse.data.success && timelineResponse.data.events && timelineResponse.data.events.length > 0) {
            const timelineEvents = timelineResponse.data.events.slice(0, 10);
            const formattedActivity: RecentActivity[] = timelineEvents.map((event: any) => {
              let activityType: 'pr_created' | 'pr_published' | 'payment' | 'campaign' = 'pr_created';
              if (event.type === 'pr_published' || event.type === 'distribution_published') {
                activityType = 'pr_published';
              } else if (event.type === 'payment_completed' || event.type === 'payment_failed') {
                activityType = 'payment';
              } else if (event.type === 'campaign_created' || event.type === 'campaign_started') {
                activityType = 'campaign';
              }

              const eventDate = new Date(event.timestamp);
              const now = new Date();
              const diffInMs = now.getTime() - eventDate.getTime();
              const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
              const diffInDays = Math.floor(diffInHours / 24);
              
              let timestamp = '';
              if (diffInMs < 60000) {
                timestamp = 'Just now';
              } else if (diffInHours < 1) {
                const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
                timestamp = `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
              } else if (diffInHours < 24) {
                timestamp = `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
              } else if (diffInDays < 7) {
                timestamp = `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
              } else {
                timestamp = eventDate.toLocaleDateString();
              }

              return {
                id: event.id,
                type: activityType,
                title: event.title,
                timestamp: timestamp,
                status: event.status.charAt(0).toUpperCase() + event.status.slice(1).replace('_', ' '),
              };
            });
            setRecentActivity(formattedActivity);
          } else {
            setRecentActivity([]);
          }
        } catch (timelineErr) {
          console.error('Error fetching timeline events:', timelineErr);
          // Keep empty array if timeline fetch fails
          setRecentActivity([]);
        }
      }

      // Set upcoming tasks from dashboard data
      if (dashboardData.upcomingTasks && Array.isArray(dashboardData.upcomingTasks)) {
        setUpcomingTasks(dashboardData.upcomingTasks);
      } else {
        setUpcomingTasks([]);
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate budget usage percentage
  // Only show budget percentage if there are active campaigns with budgets
  // and the budget is greater than or equal to spent (to avoid misleading percentages)
  const budgetUsagePercent = (stats.totalBudget > 0 && stats.totalBudget >= stats.totalSpent)
    ? Math.round((stats.totalSpent / stats.totalBudget) * 100) 
    : stats.totalBudget > 0 && stats.totalSpent > stats.totalBudget
    ? 100 // Cap at 100% if spent exceeds budget
    : 0;

  // Calculate monthly goal progress (assuming 100K impressions as monthly goal, can be made configurable)
  const monthlyGoal = 100000; // Can be made dynamic from user settings
  const goalProgress = monthlyGoal > 0 
    ? Math.min(100, Math.round((stats.totalImpressions / monthlyGoal) * 100)) 
    : 0;

  const quickStats: QuickStat[] = [
    {
      label: 'Total PRs',
      value: stats.totalPRs,
      change: 0, // Would need historical data to calculate
      icon: <ArticleIcon style={{ fontSize: 40 }} />,
      color: '#0927eb',
    },
    {
      label: 'Published',
      value: stats.publishedPRs,
      change: 0, // Would need historical data to calculate
      icon: <CheckCircleIcon style={{ fontSize: 40 }} />,
      color: '#ff5200',
    },
    {
      label: 'Active Campaigns',
      value: stats.activeCampaigns,
      change: 0, // Would need historical data to calculate
      icon: <CampaignIcon style={{ fontSize: 40 }} />,
      color: '#cdcdcd',
    },
    {
      label: 'Success Rate',
      value: `${stats.successRate}%`,
      change: 0, // Would need historical data to calculate
      icon: <TrendingUpIcon style={{ fontSize: 40 }} />,
      color: '#ff5200',
    },
  ];

  const quickActions = [
    {
      title: 'Create New PR',
      description: 'Start writing your press release',
      icon: <AddIcon style={{ fontSize: 32 }} />,
      color: '#ff5200',
      action: () => navigate('/create-pr'),
    },
    {
      title: 'View Analytics',
      description: 'Track your performance',
      icon: <AnalyticsIcon style={{ fontSize: 32 }} />,
      color: '#cdcdcd',
      action: () => navigate('/analytics'),
    },
    {
      title: 'Manage Campaigns',
      description: 'Review active campaigns',
      icon: <CampaignIcon style={{ fontSize: 32 }} />,
      color: '#ff5200',
      action: () => navigate('/campaigns'),
    },
    {
      title: 'PR Library',
      description: 'Browse all press releases',
      icon: <ArticleIcon style={{ fontSize: 32 }} />,
      color: '#cdcdcd',
      action: () => navigate('/pr-library'),
    },
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'pr_published':
        return <CheckCircleIcon style={{ color: '#ff5200' }} />;
      case 'pr_created':
        return <EditIcon style={{ color: '#0927eb' }} />;
      case 'payment':
        return <PaymentIcon style={{ color: '#cdcdcd' }} />;
      case 'campaign':
        return <CampaignIcon style={{ color: '#0927eb' }} />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return '#F44336';
      case 'medium':
        return '#ff5200';
      case 'low':
        return '#0927eb';
      default:
        return '#9e9e9e';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" style={{ padding: '24px', textAlign: 'center' }}>
        <CircularProgress style={{ marginTop: '100px' }} />
        <Typography variant="body1" style={{ marginTop: '16px', color: '#666' }}>
          Loading dashboard data...
        </Typography>
      </Container>
    );
  }

  // Show UI with default values even if there's an error
  // The error will be displayed as a warning banner at the top

  return (
    <Container maxWidth="xl" style={{ padding: '24px' }}>
      {/* Error Banner - Show at top if there's an error */}
      {error && (
        <Alert 
          severity="warning" 
          style={{ marginBottom: '24px' }}
          action={
            <Button
              size="small"
              onClick={fetchDashboardData}
              style={{ color: 'inherit' }}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Welcome Header */}
      <Box style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Typography
            variant="h4"
            style={{
              fontWeight: 'bold',
              color: '#0927eb',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <SpeedIcon style={{ fontSize: '32px' }} />
            Dashboard
          </Typography>
          <Typography variant="body1" style={{ color: '#666' }}>
            Welcome back, {user?.firstName || 'User'}! Here's your PR performance overview.
          </Typography>
        </div>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchDashboardData}
          disabled={loading}
          style={{
            borderColor: '#0927eb',
            color: '#0927eb',
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} style={{ marginBottom: '32px' }}>
        {quickStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              style={{
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`,
                color: 'white',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}
            >
              <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flex: 1,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Typography variant="h3" style={{ fontWeight: 'bold', color: 'white' }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" style={{ color: 'white', opacity: 0.9 }}>
                      {stat.label}
                    </Typography>
                    {stat.change !== 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                      <TrendingUpIcon style={{ fontSize: '16px', marginRight: '4px' }} />
                      <Typography variant="body2" style={{ color: 'white' }}>
                          {stat.change > 0 ? '+' : ''}{stat.change}% this month
                      </Typography>
                    </div>
                    )}
                    {stat.label === 'Total PRs' && stats.totalPRs > 0 && (
                      <Typography variant="caption" style={{ color: 'white', opacity: 0.8, marginTop: '4px', display: 'block', minHeight: '16px' }}>
                        {stats.publishedPRs} published, {stats.pendingPRs} pending
                      </Typography>
                    )}
                    {stat.label === 'Published' && stats.publishedArticlesCount > 0 && (
                      <Typography variant="caption" style={{ color: 'white', opacity: 0.8, marginTop: '4px', display: 'block', minHeight: '16px' }}>
                        {stats.publishedArticlesCount} articles live
                      </Typography>
                    )}
                    {stat.label === 'Active Campaigns' && (
                      <Typography variant="caption" style={{ color: 'white', opacity: 0.8, marginTop: '4px', display: 'block', minHeight: '16px' }}>
                        {campaignStatsData ? (
                          <>
                            {campaignStatsData.activeCampaigns} active
                          </>
                        ) : (
                          stats.activeCampaigns > 0 ? `${stats.activeCampaigns} active` : 'No active campaigns'
                        )}
                      </Typography>
                    )}
                    {stat.label === 'Success Rate' && (
                      <Typography variant="caption" style={{ color: 'white', opacity: 0.8, marginTop: '4px', display: 'block', minHeight: '16px' }}>
                        {stats.totalPRs > 0 ? `${stats.publishedPRs} of ${stats.totalPRs} published` : 'No PRs yet'}
                      </Typography>
                    )}
                  </div>
                  <div style={{ opacity: 0.3 }}>{stat.icon}</div>
                </div>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>


      {/* Performance Overview */}
      <Grid container spacing={3} style={{ marginBottom: '32px' }}>
        <Grid item xs={12} md={8}>
          <Card style={{ borderRadius: '12px', height: '100%' }}>
            <CardContent style={{ paddingBottom: '16px' }}>
              <Typography
                variant="h6"
                style={{ fontWeight: 'bold', marginBottom: '20px', color: '#000' }}
              >
                Performance Highlights
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper
                    style={{
                      padding: '16px',
                      borderRadius: '12px',
                      background: '#0927eb',
                      color: 'white',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <VisibilityIcon style={{ marginRight: '8px' }} />
                      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                        Total Impressions
                      </Typography>
                    </div>
                    <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                      {stats.totalImpressions >= 1000000
                        ? `${(stats.totalImpressions / 1000000).toFixed(1)}M`
                        : stats.totalImpressions >= 1000
                        ? `${(stats.totalImpressions / 1000).toFixed(1)}K`
                        : stats.totalImpressions.toLocaleString()}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={goalProgress}
                      style={{
                        marginTop: '12px',
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        height: '6px',
                        borderRadius: '3px',
                      }}
                      sx={{
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#ffffff',
                        },
                      }}
                    />
                    <Typography variant="body2" style={{ marginTop: '8px', opacity: 0.9 }}>
                      {goalProgress}% of monthly goal ({monthlyGoal >= 1000000 ? `${(monthlyGoal / 1000000).toFixed(1)}M` : `${(monthlyGoal / 1000).toFixed(0)}K`} impressions)
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    style={{
                      padding: '16px',
                      borderRadius: '12px',
                      background: '#cdcdcd',
                      color: '#000',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <PaymentIcon style={{ marginRight: '8px' }} />
                      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                        Total Spent
                      </Typography>
                    </div>
                    <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                      ${stats.totalSpent.toLocaleString()}
                    </Typography>
                    {creditsData && creditsData.credits && creditsData.credits.used > 0 && (
                      <Typography variant="body2" style={{ marginTop: '4px', color: '#666', fontSize: '0.85rem' }}>
                        {creditsData.credits.used} credit{creditsData.credits.used !== 1 ? 's' : ''} used from packages
                      </Typography>
                    )}
                    {stats.totalBudget > 0 && stats.activeCampaigns > 0 ? (
                      <>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(budgetUsagePercent, 100)}
                          style={{
                            marginTop: '12px',
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            height: '6px',
                            borderRadius: '3px',
                          }}
                          sx={{
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: budgetUsagePercent > 90 ? '#F44336' : budgetUsagePercent > 70 ? '#ff5200' : '#0927eb',
                            },
                          }}
                        />
                        
                      </>
                    ) : (
                      <Typography variant="body2" style={{ marginTop: '12px', color: '#666' }}>
                        Total amount spent on all payments
                      </Typography>
                    )}
                    {creditsData && creditsData.credits && creditsData.credits.remaining > 0 && (
                      <Typography variant="caption" style={{ marginTop: '4px', display: 'block', color: '#4CAF50', fontWeight: 600 }}>
                        {creditsData.credits.remaining} credit{creditsData.credits.remaining !== 1 ? 's' : ''} remaining
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper
                    style={{
                      padding: '16px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #0927eb 0%, #3A4FED 100%)',
                      color: 'white',
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                          <WhatshotIcon style={{ marginRight: '8px', fontSize: '28px' }} />
                          <Typography variant="h5" style={{ fontWeight: 'bold' }}>
                            {stats.roi > 0 ? `+${stats.roi}` : stats.roi === 0 && (stats.totalImpressions > 0 || stats.totalClicks > 0) ? '0' : 'N/A'}%
                          </Typography>
                        </div>
                        <Typography variant="body2" style={{ opacity: 0.9 }}>
                          Return on Investment
                        </Typography>
                        {stats.roi === 0 && stats.totalImpressions === 0 && stats.totalClicks === 0 && (
                          <Typography variant="caption" style={{ opacity: 0.7, fontSize: '0.7rem', display: 'block', marginTop: '4px' }}>
                            Calculate after engagement data
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                          {stats.publishedPRs}/{stats.totalPRs}
                        </Typography>
                        <Typography variant="body2" style={{ opacity: 0.9 }}>
                          Publication Rate ({stats.successRate}%)
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                          {stats.publishedArticlesCount || stats.publishedPRs}
                        </Typography>
                        <Typography variant="body2" style={{ opacity: 0.9 }}>
                          Published Articles
                        </Typography>
                      </Grid>
                    </Grid>
                    {stats.totalSessions > 0 && (
                      <Grid container spacing={2} alignItems="center" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                            {stats.totalSessions.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" style={{ opacity: 0.9 }}>
                            Total Sessions
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                            {stats.totalBacklinks.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" style={{ opacity: 0.9 }}>
                            Total Backlinks
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                            {stats.averageCTR.toFixed(2)}%
                          </Typography>
                          <Typography variant="body2" style={{ opacity: 0.9 }}>
                            Average CTR
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Upcoming Tasks */}
        <Grid item xs={12} md={4}>
          <Card style={{ borderRadius: '12px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingBottom: '16px' }}>
              <Typography
                variant="h6"
                style={{ fontWeight: 'bold', marginBottom: '16px', color: '#000' }}
              >
                <ScheduleIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                Upcoming Tasks
              </Typography>
              {upcomingTasks.length === 0 ? (
                <Box style={{ textAlign: 'center', padding: '40px 0', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    No upcoming tasks
                  </Typography>
                </Box>
              ) : (
                <Box style={{ flex: 1, overflow: 'hidden' }}>
                  <List style={{ overflow: 'hidden', padding: 0 }}>
                    {upcomingTasks.slice(0, 5).map((task, index) => (
                      <React.Fragment key={`${task.type}-${task.prId || task.distributionId || index}`}>
                        <ListItem style={{ padding: '12px 0' }}>
                          <ListItemIcon>
                            <div
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: getPriorityColor(task.priority),
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {task.task}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" style={{ color: '#666' }}>
                                Deadline: {task.deadline}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < Math.min(upcomingTasks.length, 5) - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              )}
              <Button
                variant="outlined"
                fullWidth
                style={{
                  marginTop: '16px',
                  borderColor: '#0927eb',
                  color: '#0927eb',
                  fontWeight: 'bold',
                }}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/timeline')}
              >
                View All Tasks
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Card style={{ borderRadius: '12px', marginBottom: '32px' }}>
        <CardContent>
          <Typography
            variant="h6"
            style={{ fontWeight: 'bold', marginBottom: '24px', color: '#000' }}
          >
            Quick Actions
          </Typography>
          <Grid container spacing={3}>
            {quickActions.map((action, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  onClick={action.action}
                  style={{
                    padding: '24px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '2px solid transparent',
                    textAlign: 'center',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.borderColor = action.color;
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                >
                  <div
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '12px',
                      background: action.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      color: action.color === '#cdcdcd' ? '#000' : 'white',
                    }}
                  >
                    {action.icon}
                  </div>
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    {action.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Recent Activity & Insights */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card style={{ borderRadius: '12px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="h6"
                style={{ fontWeight: 'bold', marginBottom: '16px', color: '#000' }}
              >
                <TimelineIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                Recent Activity
              </Typography>
              {recentActivity.length === 0 ? (
                <Box style={{ textAlign: 'center', padding: '40px 0', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    No recent activity to display
                  </Typography>
                </Box>
              ) : (
                <Box style={{ flex: 1, overflow: 'hidden' }}>
                  <List style={{ overflow: 'hidden', padding: 0 }}>
                    {recentActivity.slice(0, 5).map((activity, index) => (
                      <React.Fragment key={activity.id}>
                        <ListItem style={{ padding: '12px 0' }}>
                          <ListItemIcon>{getActivityIcon(activity.type)}</ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body1" style={{ fontWeight: 600 }}>
                                {activity.title}
                              </Typography>
                            }
                            secondary={
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
                                <Typography variant="caption" style={{ color: '#666' }}>
                                  {activity.timestamp}
                                </Typography>
                                <Chip
                                  label={activity.status}
                                  size="small"
                                  style={{
                                    backgroundColor:
                                      activity.status === 'Published' || activity.status === 'Completed'
                                        ? '#ff5200'
                                        : activity.status === 'Active'
                                        ? '#0927eb'
                                        : '#cdcdcd',
                                    color: 'white',
                                    fontSize: '11px',
                                    height: '20px',
                                  }}
                                />
                              </div>
                            }
                          />
                        </ListItem>
                        {index < Math.min(recentActivity.length, 5) - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              )}
              <Button
                variant="text"
                fullWidth
                style={{ marginTop: '8px', color: '#0927eb', fontWeight: 'bold' }}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/timeline')}
              >
                View All Activity
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card style={{ borderRadius: '12px', marginBottom: '24px', display: 'flex', flexDirection: 'column' }}>
            <CardContent style={{ display: 'flex', flexDirection: 'column', paddingBottom: '16px' }}>
              <Typography
                variant="h6"
                style={{ fontWeight: 'bold', marginBottom: '16px', color: '#000' }}
              >
                <StarIcon style={{ verticalAlign: 'middle', marginRight: '8px', color: '#ff5200' }} />
                Pro Tips
              </Typography>
              <Box>
                {stats.totalImpressions > 0 && stats.totalClicks > 0 && (
                  <Alert severity="success" style={{ marginBottom: '12px', borderRadius: '8px' }}>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      Performance Insight
                    </Typography>
                    <Typography variant="caption">
                      Your PRs have generated {stats.totalImpressions.toLocaleString()} impressions with a {stats.averageCTR.toFixed(2)}% CTR. 
                      {stats.averageCTR > 5 ? ' Excellent engagement!' : stats.averageCTR > 3 ? ' Good performance!' : ' Keep optimizing!'}
                    </Typography>
                  </Alert>
                )}
                {stats.publishedArticlesCount > 0 && (
                <Alert severity="info" style={{ marginBottom: '12px', borderRadius: '8px' }}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                      Published Articles
                  </Typography>
                  <Typography variant="caption">
                      You have {stats.publishedArticlesCount} published article{stats.publishedArticlesCount > 1 ? 's' : ''} 
                      {stats.totalBacklinks > 0 ? ` with ${stats.totalBacklinks} backlink${stats.totalBacklinks > 1 ? 's' : ''}.` : '.'}
                  </Typography>
                </Alert>
                )}
                {stats.totalSessions > 0 && (
                  <Alert severity="info" style={{ marginBottom: '12px', borderRadius: '8px' }}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                      Traffic Analytics
                  </Typography>
                  <Typography variant="caption">
                      Your published articles have generated {stats.totalSessions.toLocaleString()} session{stats.totalSessions > 1 ? 's' : ''} 
                      {stats.totalConversions > 0 ? ` and ${stats.totalConversions} conversion${stats.totalConversions > 1 ? 's' : ''}.` : '.'}
                  </Typography>
                </Alert>
                )}
                {stats.pendingPRs > 0 && (
                  <Alert severity="warning" style={{ marginBottom: '12px', borderRadius: '8px' }}>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      Action Needed
                    </Typography>
                    <Typography variant="caption">
                      {stats.pendingPRs} PR{stats.pendingPRs > 1 ? 's are' : ' is'} pending review. Complete{' '}
                      {stats.pendingPRs > 1 ? 'them' : 'it'} to improve your success rate.
                    </Typography>
                  </Alert>
                )}
                {stats.distributedPRs > 0 && stats.distributedPRs > stats.publishedPRs && (
                  <Alert severity="info" style={{ borderRadius: '8px' }}>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      Distribution Status
                    </Typography>
                    <Typography variant="caption">
                      {stats.distributedPRs - stats.publishedPRs} PR{stats.distributedPRs - stats.publishedPRs > 1 ? 's are' : ' is'} distributed and awaiting publication.
                    </Typography>
                  </Alert>
                )}
                {stats.totalPRs === 0 && (
                  <Alert severity="info" style={{ borderRadius: '8px' }}>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      Get Started
                    </Typography>
                    <Typography variant="caption">
                      Create your first press release to start tracking performance and analytics.
                    </Typography>
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card style={{ borderRadius: '12px', background: 'linear-gradient(135deg, #0927eb 0%, #3A4FED 100%)', color: 'white' }}>
            <CardContent>
              <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '12px' }}>
                Upgrade Your Reach
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '16px', opacity: 0.9 }}>
                Unlock advanced analytics and premium distribution channels to boost your PR performance.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                style={{
                  backgroundColor: '#000000',
                  color: 'white',
                  fontWeight: 'bold',
                }}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/pricing')}
              >
                Explore Pricing
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;


